import { Navigate } from 'react-router';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';

export const RegistrationRedirect = () => {
  const user = useUserSelector();
  const isWorkerClassificationEnabled = Boolean(useIsWorkerClassificationEnabled());
  const target = inferRedirectTarget(user, { isWorkerClassificationEnabled });
  return <Navigate to={target} replace />;
};

interface IInferRedirectTargetOptions {
  isWorkerClassificationEnabled?: boolean;
}

interface IUserShape {
  first_name?: string | null;
  last_name?: string | null;
  contact_number?: string | null;
  office_name?: string | null;
  address?: Array<{ formatted_address?: string | null } | null> | null;
  office_info?: {
    officeType?: string | null;
    is_info_asked?: boolean | null;
    timePerPatientRDH?: string | null;
    timePerPatientDA?: string | null;
    timePerPatientDN?: string | null;
    isAssistedHygiene?: boolean | null;
  } | null;
  parkingInformation?: {
    parkingType?: string | null;
    isParkingFree?: boolean | null;
  } | null;
}

const nonEmpty = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const isStep1Complete = (u: IUserShape): boolean =>
  nonEmpty(u.first_name) && nonEmpty(u.last_name) && nonEmpty(u.contact_number);

// Specialty lives on userEducation, not on the user payload — not checked here.
const isStep2Complete = (u: IUserShape): boolean =>
  nonEmpty(u.office_name) &&
  Boolean(u.address?.[0]?.formatted_address) &&
  nonEmpty(u.office_info?.officeType);

// is_info_asked is the BE signal that Step 3 was submitted; falls back to
// per-field checks when absent.
const isStep3Complete = (u: IUserShape): boolean => {
  if (u.office_info?.is_info_asked === true) return true;
  const info = u.office_info ?? {};
  const parking = u.parkingInformation ?? {};
  return (
    nonEmpty(info.timePerPatientRDH) &&
    nonEmpty(info.timePerPatientDA) &&
    nonEmpty(info.timePerPatientDN) &&
    typeof info.isAssistedHygiene === 'boolean' &&
    nonEmpty(parking.parkingType) &&
    typeof parking.isParkingFree === 'boolean'
  );
};

export const inferRedirectTarget = (
  user: unknown,
  { isWorkerClassificationEnabled = false }: IInferRedirectTargetOptions = {},
): string => {
  if (!user) return routes.signUpRedesign.login;

  const u = user as IUserShape;

  if (!isStep1Complete(u)) return routes.signUpRedesign.yourInfo;
  if (!isStep2Complete(u)) return routes.signUpRedesign.officeDetails;
  if (!isStep3Complete(u)) return routes.signUpRedesign.officeSetup;

  if (isWorkerClassificationEnabled && u.office_info?.is_info_asked) {
    return routes.signUpRedesign.workerType;
  }

  return routes.signUpRedesign.officeSetup;
};
